/**
 * ================================================================
 * AVATAR COMPONENT - Simple avatar display component
 * ================================================================
 *
 * Simple avatar component for displaying user profile images
 * with fallback support
 *
 * @author MeBi Team
 * @version 1.0.0
 */

import React from "react";
import { cn } from "./utils";

interface AvatarProps {
  className?: string;
  children: React.ReactNode;
}

interface AvatarImageProps {
  src?: string;
  alt?: string;
  className?: string;
  referrerPolicy?: React.ImgHTMLAttributes<HTMLImageElement>["referrerPolicy"];
  crossOrigin?: React.ImgHTMLAttributes<HTMLImageElement>["crossOrigin"];
  loading?: React.ImgHTMLAttributes<HTMLImageElement>["loading"];
  decoding?: React.ImgHTMLAttributes<HTMLImageElement>["decoding"];
}

interface AvatarFallbackProps {
  className?: string;
  children: React.ReactNode;
}

export const Avatar: React.FC<AvatarProps> = ({ className, children }) => {
  return (
    <div
      className={cn(
        "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const AvatarImage: React.FC<AvatarImageProps> = ({
  src,
  alt,
  className,
  referrerPolicy,
  crossOrigin,
  loading = "lazy",
  decoding = "async",
}) => {
  const [hasError, setHasError] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement | null>(null);

  React.useEffect(() => {
    setHasError(false);

    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      return;
    }
  }, [src]);

  if (!src || hasError) {
    return null;
  }

  return (
    <img
      ref={imgRef}
      className={cn("aspect-square h-full w-full", className)}
      src={src}
      alt={alt}
      loading={loading}
      decoding={decoding}
      crossOrigin={crossOrigin}
      referrerPolicy={referrerPolicy}
      onError={() => setHasError(true)}
    />
  );
};

export const AvatarFallback: React.FC<AvatarFallbackProps> = ({
  className,
  children,
}) => {
  return (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-neutral-100",
        className,
      )}
    >
      {children}
    </div>
  );
};
